import FormHelperText from '@mui/material/FormHelperText';
import Skeleton from '@mui/material/Skeleton';
import type { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';

type LabelTextDisplayProps = Pick<TextFieldProps, 'label'>;

export interface TextDisplayProps
	extends Pick<TextFieldProps, 'label' | 'color' | 'helperText' | 'sx'>,
		LabelTextDisplayProps {
	value: React.ReactNode;
	/**
	 * Element placed before the children.
	 */
	startIcon?: React.ReactNode;
	/**
	 * Element placed after the children.
	 */
	endIcon?: React.ReactNode;

	loading?: boolean;
}

const Container = styled('div')(({ theme }) => ({
	'& svg': {
		verticalAlign: 'bottom',
	},
	'.text-display__value': {
		flex: 1,
	},
	'.text-display__start-adornment': {
		alignSelf: 'flex-start',
		marginRight: theme.spacing(1),
		marginLeft: theme.spacing(-0.25),
	},
	'.text-display__end-adornment': {
		alignSelf: 'flex-end',
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(-0.25),
	},
}));

export const LabelTextDisplay = ({ label }: LabelTextDisplayProps) => (
	<Typography
		className="text-display__label"
		component="label"
		variant="caption"
		sx={(theme) => ({
			fontSize: '0.875rem',
			color: alpha(theme.palette.primary.main, 0.6),
		})}>
		{label}
	</Typography>
);

export const TextDisplay = ({
	label,
	value,
	color,
	helperText,
	startIcon,
	endIcon,
	loading = false,
	sx = [],
}: TextDisplayProps): React.JSX.Element => {
	const displayComponent = loading ? <Skeleton variant="rounded" /> : value;

	return (
		<Container className="text-display__root" sx={sx}>
			<LabelTextDisplay label={label} />
			<Typography
				className="text-display__value-base"
				component="div"
				variant="subtitle1"
				sx={{
					display: 'flex',
					alignItems: 'flex-end',
					fontWeight: 'normal',
					color: color ?? 'primary',
				}}>
				{startIcon && (
					<span className="text-display__start-adornment">
						{startIcon}
					</span>
				)}
				<div className="text-display__value">
					{displayComponent != null &&
					typeof displayComponent === 'object' ? (
						displayComponent
					) : (
						<p>
							{Boolean(displayComponent)
								? (displayComponent as string)
								: '\u00A0'}
						</p>
					)}
				</div>
				{endIcon && (
					<span className="text-display__end-adornment">
						{endIcon}
					</span>
				)}
			</Typography>
			{helperText && <FormHelperText>{helperText}</FormHelperText>}
		</Container>
	);
};
